import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Eye } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import { Button } from '../../../components/ui/button';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import Drawer from '../../../components/common/Drawer';
import StatusChip from '../../../components/common/StatusChip';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import { mamManagerService } from '../services/mamService';
import { formatCurrency, formatPercent, formatDate } from '../utils';
import { MAM_ROUTES } from '../../../constants/routeConstants';
import type { MAMManager } from '../types';

export default function MAMManagers() {
  const navigate = useNavigate();
  const [managers, setManagers] = useState<MAMManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedManager, setSelectedManager] = useState<MAMManager | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadManagers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mamManagerService.getAll();
      setManagers(data);
    } catch {
      setError('Failed to fetch MAM Managers list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadManagers();
  }, []);

  const handleRowClick = (mgr: MAMManager) => {
    setSelectedManager(mgr);
    setDrawerOpen(true);
  };

  const columns: DataTableColumn<MAMManager>[] = [
    {
      id: 'name',
      header: 'Manager / Account',
      sortable: true,
      accessor: mgr => (
        <div className="flex items-center space-x-3">
          {mgr.avatar ? (
            <img
              src={mgr.avatar}
              alt={mgr.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center">
              {mgr.name.charAt(0)}
            </div>
          )}
          <div>
            <p className="font-bold text-foreground text-xs">{mgr.name}</p>
            <p className="text-[10px] text-muted-foreground">
              {mgr.accountNumber} • {mgr.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'totalAUM',
      header: 'Total AUM',
      sortable: true,
      align: 'right',
      accessor: mgr => (
        <span className="font-bold text-foreground">
          {formatCurrency(mgr.totalAUM)}
        </span>
      ),
    },
    {
      id: 'totalReturn',
      header: 'Total Return',
      sortable: true,
      align: 'right',
      accessor: mgr => (
        <span
          className={`font-bold ${mgr.totalReturn >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}
        >
          {formatPercent(mgr.totalReturn)}
        </span>
      ),
    },
    {
      id: 'activeClients',
      header: 'Active Clients',
      sortable: true,
      align: 'right',
      accessor: mgr => (
        <span className="font-semibold">{mgr.activeClients} accounts</span>
      ),
    },
    {
      id: 'performanceFee',
      header: 'Perf. Fee',
      sortable: true,
      align: 'right',
      accessor: mgr => <span>{mgr.performanceFee}%</span>,
    },
    {
      id: 'riskLevel',
      header: 'Risk Level',
      sortable: true,
      align: 'center',
      accessor: mgr => (
        <span className="capitalize font-semibold text-xs text-muted-foreground">
          {mgr.riskLevel}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      align: 'center',
      accessor: mgr => <StatusChip status={mgr.status} size="sm" />,
    },
    {
      id: 'actions',
      header: 'Action',
      align: 'right',
      accessor: mgr => (
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={e => {
            e.stopPropagation();
            navigate(`${MAM_ROUTES.MAM_MANAGER_DETAILS}/${mgr.id}`);
          }}
        >
          <Eye className="w-3.5 h-3.5 mr-1" />
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Managers" />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Managers" />
        <ErrorState message={error} onRetry={loadManagers} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Managers"
        description="Directory of master traders and multi-account fund managers"
      >
        <Button variant="outline" size="sm" onClick={loadManagers}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <Button
          size="sm"
          onClick={() => navigate(MAM_ROUTES.MAM_CREATE_MANAGER)}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Create Manager
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={managers}
        searchKeys={['name', 'email', 'accountNumber']}
        searchPlaceholder="Search managers by name, email, or account #..."
        onRowClick={handleRowClick}
        initialPageSize={8}
      />

      {/* Quick Detail Drawer */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedManager?.name || 'Manager Details'}
        subtitle={`Account: ${selectedManager?.accountNumber}`}
      >
        {selectedManager && (
          <div className="space-y-4 text-xs">
            <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Manager ID:</span>
                <span className="font-mono font-semibold">
                  {selectedManager.id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-semibold">{selectedManager.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Joined Date:</span>
                <span>{formatDate(selectedManager.joinedAt)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 border border-border rounded-xl">
                <p className="text-[10px] text-muted-foreground">Total AUM</p>
                <p className="text-base font-bold text-foreground">
                  {formatCurrency(selectedManager.totalAUM)}
                </p>
              </div>
              <div className="p-3 border border-border rounded-xl">
                <p className="text-[10px] text-muted-foreground">
                  Total Return
                </p>
                <p className="text-base font-bold text-emerald-600">
                  {formatPercent(selectedManager.totalReturn)}
                </p>
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <p className="font-semibold text-foreground">
                Fee Schedule & Parameters
              </p>
              <div className="flex justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Performance Fee:</span>
                <span className="font-bold">
                  {selectedManager.performanceFee}%
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Management Fee:</span>
                <span className="font-bold">
                  {selectedManager.managementFee}% p.a.
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border/50">
                <span className="text-muted-foreground">Leverage:</span>
                <span className="font-mono font-semibold">
                  {selectedManager.leverage}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">Win Rate:</span>
                <span className="font-bold text-emerald-600">
                  {selectedManager.winRate}%
                </span>
              </div>
            </div>

            <Button
              className="w-full mt-4"
              onClick={() => {
                setDrawerOpen(false);
                navigate(
                  `${MAM_ROUTES.MAM_MANAGER_DETAILS}/${selectedManager.id}`
                );
              }}
            >
              Open Full Manager Profile Page
            </Button>
          </div>
        )}
      </Drawer>
    </div>
  );
}
